package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.AdminMenuItemDto
import com.qrkontaq.data.dto.AdminRestaurantDto
import com.qrkontaq.data.dto.CloseTableLoyaltyDto
import com.qrkontaq.data.dto.CreateOrderRequestDto
import com.qrkontaq.data.dto.LoyaltyCandidateDto
import com.qrkontaq.data.dto.LoyaltyGuestDto
import com.qrkontaq.data.dto.OrderDto
import com.qrkontaq.data.dto.OrderItemDto
import com.qrkontaq.data.dto.OrderItemRequestDto
import com.qrkontaq.data.dto.StaffMemberDto
import com.qrkontaq.data.dto.TableDto
import com.qrkontaq.data.dto.formatMoneyMinor
import com.qrkontaq.data.dto.localized
import com.qrkontaq.core.auth.Session
import com.qrkontaq.data.repository.AdminRepository
import com.qrkontaq.data.repository.CloseTableResult
import com.qrkontaq.data.repository.LoyaltyIdentifyResult
import com.qrkontaq.ui.components.forms.TextInput
import com.qrkontaq.ui.components.notifications.LocalToastState
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.tenant.MenuIcons
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLVideoElement
import kotlin.math.roundToInt

// ---------- Helpers ----------

internal val OrderDto.totalMinor: Int get() = items.sumOf { it.priceMinor * it.qty }

/** Human label for a staff member: full name, else the email local part. */
internal fun staffDisplayName(member: StaffMemberDto): String =
    member.fullName?.takeIf { it.isNotBlank() }
        ?: member.email?.substringBefore('@')?.takeIf { it.isNotBlank() }
        ?: "—"

internal fun OrderDto.createdMs(): Double =
    kotlin.js.Date(createdAt).getTime().takeIf { !it.isNaN() } ?: kotlin.js.Date.now()

internal fun formatMinor(minor: Int, currency: String): String {
    val whole = minor / 100
    val cents = minor % 100
    val symbol = com.qrkontaq.data.dto.currencySymbol(currency)
    return if (cents == 0) "$whole $symbol" else "$whole.${cents.toString().padStart(2, '0')} $symbol"
}

/** Accepts a raw waiter input or scanned payload: "483920" or "QRKL:483920" → "483920". */
internal fun normalizeLoyaltyCode(raw: String): String? =
    Regex("^(?:QRKL:)?(\\d{6})$").find(raw.trim().uppercase())?.groupValues?.get(1)

/** BarcodeDetector is Chromium-only for now; without it we silently keep manual input. */
private fun barcodeDetectorAvailable(): Boolean =
    js("typeof BarcodeDetector !== 'undefined'") as Boolean

internal fun statusLabel(status: String): String =
    when (status) {
        "NEW" -> AdminT.statusNew
        "COOKING" -> AdminT.statusCooking
        "READY" -> AdminT.statusReady
        "SERVED" -> AdminT.statusServed
        else -> status
    }

internal fun statusTone(status: String): String =
    when (status) {
        "COOKING" -> "warning"
        "READY" -> "success"
        else -> "neutral"
    }

private fun elapsedMin(
    createdAt: Double,
    now: Double,
): Long = ((now - createdAt) / 60_000).toLong().coerceAtLeast(0)

// ---------- Store ----------

/**
 * Shared cache over the backend orders API: the tables screen and the kitchen
 * board read the same snapshot; callers poll via [refresh].
 */
object OrdersStore {
    private val state = mutableStateOf<List<OrderDto>>(emptyList())
    private var loadedRid: String? = null

    /** Current snapshot; reading inside a composable subscribes to updates. */
    fun current(rid: String): List<OrderDto> {
        val snapshot = state.value
        return if (loadedRid == rid) snapshot else emptyList()
    }

    suspend fun refresh(
        repository: AdminRepository,
        rid: String,
    ) {
        runCatching { repository.orders(rid) }.getOrNull()?.let { fresh ->
            val previous = if (loadedRid == rid) state.value else null
            loadedRid = rid
            state.value = fresh
            if (previous != null) alertOnReady(previous, fresh)
        }
    }

    /**
     * Ring the waiter when the kitchen marks their ticket READY. Lives on the
     * shared store so it fires no matter which screen (tables or board)
     * happened to pull the fresh snapshot; the cook's own tap is muted by
     * [StaffAlerts.suppress]. Legacy tickets without a creator ring everyone.
     */
    private fun alertOnReady(
        previous: List<OrderDto>,
        fresh: List<OrderDto>,
    ) {
        val before = previous.associate { it.id to it.status }
        val myId = Session.userId
        val becameReady =
            fresh.filter { order ->
                order.status == "READY" &&
                    before[order.id] != null &&
                    before[order.id] != "READY" &&
                    (order.createdBy == null || order.createdBy == myId)
            }
        if (becameReady.isNotEmpty()) {
            StaffAlerts.beep()
            StaffAlerts.notify(
                AdminT.statusReady,
                becameReady.joinToString { AdminT.tableN(it.tableLabel) },
            )
        }
    }
}

// ---------- Kitchen board ----------

/** Kitchen view: three live columns — new, cooking, ready. */
@Composable
fun OrdersTab(
    repository: AdminRepository,
    rid: String,
    role: String,
) {
    // Waiters watch the board read-only; cooking statuses are the kitchen's job.
    val canCook = role == "OWNER" || role == "MANAGER" || role == "COOK" || role == "BARTENDER"

    // A cook sees dishes, a bartender sees drinks; everyone else sees the whole ticket.
    fun stationItems(order: OrderDto): List<OrderItemDto> =
        when (role) {
            "COOK" -> order.items.filter { !it.isBar }
            "BARTENDER" -> order.items.filter { it.isBar }
            else -> order.items
        }

    val scope = rememberCoroutineScope()
    var tick by remember { mutableStateOf(0) }
    var newCount by remember(rid) { mutableStateOf(-1) }
    // Managers/owners see which waiter opened each ticket; the staff list is their gate.
    var staffNames by remember(rid) { mutableStateOf<Map<String, String>>(emptyMap()) }
    if (role == "OWNER" || role == "MANAGER") {
        LaunchedEffect(rid) {
            staffNames = runCatching { repository.staff(rid) }.getOrNull()
                ?.members.orEmpty()
                .associate { it.userId to staffDisplayName(it) }
        }
    }

    // Instant refresh over WebSocket; a new incoming ticket rings.
    StaffAlerts.arm(rid)
    RealtimeUpdates(rid) { type ->
        scope.launch {
            OrdersStore.refresh(repository, rid)
            tick++
        }
    }
    LaunchedEffect(rid) {
        while (true) {
            OrdersStore.refresh(repository, rid)
            tick++
            kotlinx.coroutines.delay(20_000)
        }
    }
    val now = remember(tick) { kotlin.js.Date.now() }
    // Drop tickets with nothing for this station: a bartender never sees a food-only order.
    val orders = OrdersStore.current(rid)
        .filter { it.status != "SERVED" }
        .filter { stationItems(it).isNotEmpty() }

    // Ring when a fresh NEW ticket appears (not on the kitchen's own advances).
    val nowNew = orders.count { it.status == "NEW" }
    LaunchedEffect(nowNew) {
        if (newCount in 0 until nowNew) {
            StaffAlerts.beep()
            StaffAlerts.notify(AdminT.tabOrders, AdminT.statusNew)
        }
        newCount = nowNew
    }

    fun advance(
        order: OrderDto,
        status: String,
    ) {
        StaffAlerts.suppress()
        scope.launch {
            runCatching { repository.setOrderStatus(rid, order.id, status) }
            OrdersStore.refresh(repository, rid)
        }
    }

    // Three fixed lanes; they stay on screen even with nothing in them so the
    // kitchen always reads left-to-right: new → cooking → ready.
    val columns = listOf(
        BoardColumn("NEW", AdminT.statusNew, "#8A8F98", "rgba(138,143,152,.06)", "rgba(138,143,152,.16)"),
        BoardColumn("COOKING", AdminT.statusCooking, "#E8A33D", "rgba(232,163,61,.07)", "rgba(232,163,61,.22)"),
        BoardColumn("READY", AdminT.statusReady, "#0F9D6E", "rgba(15,157,110,.07)", "rgba(15,157,110,.20)"),
    )
    Div(attrs = { classes("qk-kboard", "qk-fade-in") }) {
        columns.forEach { col ->
            val cards = orders.filter { it.status == col.status }.sortedBy { it.createdMs() }
            Column(
                Modifier
                    .borderRadius(18.px)
                    .padding(11.px)
                    .styleModifier {
                        property("background", col.softBg)
                        property("border", "1px solid ${col.softBorder}")
                    }
                    .attrsModifier { classes("qk-klane") },
            ) {
                // Lane header: colour dot, title and a live count pill.
                Row(
                    Modifier.fillMaxWidth().margin(bottom = 10.px, top = 2.px, left = 2.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(
                        Modifier.size(10.px).minWidth(10.px).borderRadius(999.px)
                            .styleModifier { property("background", col.accent) },
                    )
                    SpanText(
                        col.title,
                        Modifier.fontSize(14.px).fontWeight(FontWeight.Bold)
                            .color(Brand.Charcoal).margin(left = 8.px)
                            .attrsModifier { classes("qk-display") },
                    )
                    Box(Modifier.styleModifier { property("flex", "1") })
                    Box(
                        Modifier.minWidth(22.px).borderRadius(999.px)
                            .padding(topBottom = 2.px, leftRight = 8.px)
                            .styleModifier {
                                property("background", if (cards.isEmpty()) "rgba(31,36,33,.05)" else col.accent)
                            },
                        contentAlignment = Alignment.Center,
                    ) {
                        SpanText(
                            "${cards.size}",
                            Modifier.fontSize(12.px).fontWeight(FontWeight.Bold)
                                .color(if (cards.isEmpty()) Color.rgb(154, 160, 166) else Brand.Surface),
                        )
                    }
                }
                if (cards.isEmpty()) {
                    EmptyLane()
                } else {
                    cards.forEach { order ->
                        KitchenOrderCard(
                            order,
                            stationItems(order),
                            now,
                            canCook,
                            order.createdBy?.let { staffNames[it] },
                            onAdvance = { advance(order, it) },
                        )
                    }
                }
            }
        }
    }
}

/** One kitchen lane's static style: status key, title and its tint palette. */
private data class BoardColumn(
    val status: String,
    val title: String,
    val accent: String,
    val softBg: String,
    val softBorder: String,
)

/** Placeholder shown inside a lane that currently holds no tickets. */
@Composable
private fun EmptyLane() {
    // Fills the (now full-height) lane and centres a compact dashed placeholder in it.
    Column(
        Modifier.fillMaxWidth().styleModifier { property("flex", "1") },
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Column(
            Modifier
                .padding(topBottom = 22.px, leftRight = 18.px)
                .borderRadius(14.px)
                .styleModifier { property("border", "1.5px dashed rgba(31,36,33,.11)") },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Box(
                Modifier.size(38.px).borderRadius(999.px)
                    .styleModifier { property("background", "rgba(31,36,33,.04)") },
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(MenuIcons.Receipt, 17, "#C4BEB4")
            }
            SpanText(
                AdminT.ordersColumnEmpty,
                Modifier.fontSize(12.px).fontWeight(FontWeight.Medium)
                    .color(Brand.TextSecondary).margin(top = 8.px),
            )
        }
    }
}

@Composable
private fun KitchenOrderCard(
    order: OrderDto,
    items: List<OrderItemDto>,
    now: Double,
    canCook: Boolean,
    creatorName: String?,
    onAdvance: (String) -> Unit,
) {
    val accent = when (order.status) {
        "COOKING" -> "#E8A33D"
        "READY" -> "#0F9D6E"
        else -> "#B8B2A8"
    }
    val minutes = elapsedMin(order.createdMs(), now)
    // Escalate the age pill so a stale ticket catches the eye.
    val (ageBg, ageFg) = when {
        minutes >= 20 -> "rgba(224,91,76,.14)" to "#E05B4C"
        minutes >= 10 -> "rgba(232,163,61,.18)" to "#C77F1E"
        else -> "rgba(31,36,33,.05)" to "#6B7280"
    }
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(14.px)
            .padding(top = 11.px, bottom = 11.px, left = 13.px, right = 12.px)
            .margin(bottom = 9.px)
            .styleModifier {
                property("border-left", "4px solid $accent")
                property("box-shadow", "0 1px 3px rgba(31,36,33,.05), 0 4px 14px rgba(31,36,33,.05)")
            }
            .attrsModifier { classes("qk-adm-card", "qk-rise") },
    ) {
        Row(
            Modifier.fillMaxWidth().margin(bottom = 9.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                AdminT.tableN(order.tableLabel),
                Modifier.fontSize(16.px).fontWeight(FontWeight.Bold)
                    .color(Brand.Charcoal).attrsModifier { classes("qk-display") },
            )
            Row(verticalAlignment = Alignment.CenterVertically) {
                InlineIcon(AdminIcons.Clock, 12, ageFg)
                Box(
                    Modifier.borderRadius(999.px).padding(topBottom = 3.px, leftRight = 8.px).margin(left = 5.px)
                        .styleModifier { property("background", ageBg) },
                ) {
                    SpanText(
                        AdminT.agoShort(minutes),
                        Modifier.fontSize(11.px).fontWeight(FontWeight.SemiBold)
                            .styleModifier { property("color", ageFg) }
                            .attrsModifier { classes("qk-nowrap") },
                    )
                }
            }
        }
        creatorName?.let { nm ->
            Row(
                Modifier.margin(bottom = 9.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                InlineIcon(AdminIcons.Users, 11, "#6B7280")
                SpanText(
                    AdminT.orderTakenBy(nm),
                    Modifier.fontSize(11.px).fontWeight(FontWeight.Medium)
                        .color(Brand.TextSecondary).margin(left = 5.px)
                        .attrsModifier { classes("qk-clamp1") },
                )
            }
        }
        Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(Brand.Divider).margin(bottom = 9.px))
        items.forEach { item ->
            Row(
                Modifier.fillMaxWidth().margin(bottom = 6.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    Modifier.minWidth(23.px).borderRadius(7.px).backgroundColor(Brand.WarmBackground)
                        .border(1.px, LineStyle.Solid, Brand.Divider)
                        .padding(topBottom = 2.px, leftRight = 6.px),
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        "${item.qty}",
                        Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal),
                    )
                }
                SpanText(
                    item.name,
                    Modifier.fontSize(13.px).fontWeight(FontWeight.Medium)
                        .color(Brand.Charcoal).margin(left = 9.px),
                )
            }
        }
        when {
            order.status == "NEW" && canCook -> AdminButton(
                text = AdminT.takeOrder,
                modifier = Modifier.fillMaxWidth().margin(top = 5.px),
                onClick = { onAdvance("COOKING") },
            )
            order.status == "COOKING" && canCook -> Row(
                Modifier.fillMaxWidth().margin(top = 5.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                BackAction { onAdvance("NEW") }
                Box(Modifier.styleModifier { property("flex", "1") }.margin(left = 8.px)) {
                    AdminButton(
                        text = AdminT.markReady,
                        modifier = Modifier.fillMaxWidth(),
                        onClick = { onAdvance("READY") },
                    )
                }
            }
            order.status == "READY" -> Row(
                Modifier.fillMaxWidth().margin(top = 3.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    InlineIcon(AdminIcons.Check, 13, "#0F9D6E")
                    SpanText(
                        AdminT.waitingWaiter,
                        Modifier.fontSize(12.px).fontWeight(FontWeight.Medium)
                            .color(Brand.TextSecondary).margin(left = 6.px),
                    )
                }
                if (canCook) BackAction { onAdvance("COOKING") }
            }
            else -> {}
        }
    }
}

/** Small secondary "step this ticket back one status" button. */
@Composable
private fun BackAction(onClick: () -> Unit) {
    Box(
        Modifier
            .size(34.px)
            .minWidth(34.px)
            .borderRadius(10.px)
            .backgroundColor(Brand.Surface)
            .border(1.px, LineStyle.Solid, Brand.Divider)
            .cursor(Cursor.Pointer)
            .attrsModifier {
                classes("qk-press")
                attr("role", "button")
                attr("aria-label", AdminT.orderStepBack)
                attr("title", AdminT.orderStepBack)
            }
            .onClick { onClick() },
        contentAlignment = Alignment.Center,
    ) {
        InlineIcon(AdminIcons.ChevronLeft, 15, "#6B7280")
    }
}

// ---------- Waiter sheet on the Tables screen ----------

/** Right-side drawer for a table: its orders, a dish picker and the bill. */
@Composable
fun TableOrdersSheet(
    repository: AdminRepository,
    rid: String,
    role: String,
    restaurant: AdminRestaurantDto,
    table: TableDto,
    onClose: () -> Unit,
    onDelete: (() -> Unit)?,
) {
    val scope = rememberCoroutineScope()
    val orders = OrdersStore.current(rid).filter { it.tableId == table.id }
    // A waiter (or legacy STAFF) may act only on tickets they opened; managers/owners
    // manage everything. Tickets without a creator (legacy) are open to anyone.
    val restrictToOwn = role == "WAITER" || role == "STAFF"
    val myId = Session.userId
    fun ownsOrder(o: OrderDto) = !restrictToOwn || o.createdBy == null || o.createdBy == myId
    val canCloseTable = orders.all { ownsOrder(it) }
    var pickerOpen by remember { mutableStateOf(false) }
    var menuItems by remember { mutableStateOf<List<AdminMenuItemDto>?>(null) }
    var search by remember { mutableStateOf("") }
    var draft by remember { mutableStateOf(mapOf<String, Int>()) }
    var busy by remember { mutableStateOf(false) }
    val toast = LocalToastState.current

    // ---- Loyalty (cashback) state for closing the bill ----
    val currency = restaurant.currency
    var loyaltyExpanded by remember(table.id) { mutableStateOf(false) }
    var candidates by remember(table.id) { mutableStateOf<List<LoyaltyCandidateDto>?>(null) }
    var codeInput by remember(table.id) { mutableStateOf("") }
    var identified by remember(table.id) { mutableStateOf<LoyaltyGuestDto?>(null) }
    var redeemInput by remember(table.id) { mutableStateOf("") }
    var loyaltyError by remember(table.id) { mutableStateOf<String?>(null) }
    var identifyBusy by remember { mutableStateOf(false) }
    var scanOpen by remember { mutableStateOf(false) }
    var closeResult by remember(table.id) { mutableStateOf<CloseTableLoyaltyDto?>(null) }

    // Who is at the table (loyalty guests who scanned it / rang the bell), fetched up
    // front so the waiter sees the guest and their cashback before the bill.
    LaunchedEffect(table.id) {
        OrdersStore.refresh(repository, rid)
        candidates = runCatching { repository.loyaltyCandidates(rid, table.id) }.getOrDefault(emptyList())
    }
    // A "call a waiter"/"bill" tap re-orders and highlights the guest card instantly.
    RealtimeUpdates(rid) { type ->
        if (type.lowercase().contains("call")) {
            scope.launch {
                candidates = runCatching { repository.loyaltyCandidates(rid, table.id) }.getOrDefault(emptyList())
            }
        }
    }

    fun identify() {
        val code = normalizeLoyaltyCode(codeInput) ?: return
        if (identifyBusy) return
        identifyBusy = true
        loyaltyError = null
        scope.launch {
            val result =
                runCatching { repository.identifyLoyalty(rid, code) }
                    .getOrElse { LoyaltyIdentifyResult.Error }
            identifyBusy = false
            when (result) {
                is LoyaltyIdentifyResult.Found -> identified = result.guest
                LoyaltyIdentifyResult.NotFound -> loyaltyError = AdminT.loyaltyCodeNotFound
                LoyaltyIdentifyResult.Expired -> loyaltyError = AdminT.loyaltyCodeExpired
                LoyaltyIdentifyResult.Error -> loyaltyError = AdminT.loyaltyCheckFailed
            }
        }
    }
    LaunchedEffect(pickerOpen) {
        if (pickerOpen && menuItems == null) {
            menuItems =
                runCatching { repository.getMenu(rid) }.getOrNull()
                    ?.categories.orEmpty().flatMap { it.items }.filter { it.isAvailable }
        }
    }

    // The page behind the sheet must not scroll while it is open.
    DisposableEffect(Unit) {
        val body = document.body
        val prevOverflow = body?.style?.getPropertyValue("overflow").orEmpty()
        body?.style?.setProperty("overflow", "hidden")
        onDispose { body?.style?.setProperty("overflow", prevOverflow) }
    }

    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .backgroundColor(Color.rgba(15, 20, 17, 0.45f))
            .zIndex(60)
            .attrsModifier { classes("qk-fade-in") }
            .onClick { onClose() },
    )

    // Swipe-down on the header: follow the finger, dismiss past the threshold.
    var sheetDragging by remember { mutableStateOf(false) }
    var sheetStartY by remember { mutableStateOf(0.0) }
    var sheetDy by remember { mutableStateOf(0.0) }
    DisposableEffect(sheetDragging) {
        if (!sheetDragging) {
            onDispose { }
        } else {
            val move: (org.w3c.dom.events.Event) -> Unit = { e ->
                sheetDy = kotlin.math.max(0.0, sheetPointerY(e) - sheetStartY)
            }
            val up: (org.w3c.dom.events.Event) -> Unit = {
                val shouldClose = sheetDy > SHEET_CLOSE_DRAG_PX
                sheetDragging = false
                if (shouldClose) onClose() else sheetDy = 0.0
            }
            window.addEventListener("mousemove", move)
            window.addEventListener("mouseup", up)
            window.addEventListener("touchmove", move)
            window.addEventListener("touchend", up)
            onDispose {
                window.removeEventListener("mousemove", move)
                window.removeEventListener("mouseup", up)
                window.removeEventListener("touchmove", move)
                window.removeEventListener("touchend", up)
            }
        }
    }

    Column(
        Modifier
            .position(Position.Fixed)
            .left(0.px).right(0.px).bottom(0.px)
            .fillMaxWidth()
            .maxWidth(600.px)
            .backgroundColor(Brand.WarmBackground)
            .zIndex(61)
            .padding(topBottom = 10.px, leftRight = 16.px)
            .borderRadius(topLeft = 22.px, topRight = 22.px)
            .overflow { y(Overflow.Auto) }
            .styleModifier {
                property("margin", "0 auto")
                property("max-height", "88vh")
                property("box-shadow", "0 -14px 44px rgba(31,36,33,.22)")
                property("transform", "translateY(${sheetDy}px)")
                property("transition", if (sheetDragging) "none" else "transform .25s ease")
            }
            .attrsModifier { classes("qk-adm-sheet", "qk-hide-scrollbar") },
    ) {
        // Grab handle — the swipe-down zone together with the header below.
        Box(
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 4.px)
                .cursor(Cursor.Grab)
                .styleModifier { property("touch-action", "none") }
                .attrsModifier {
                    onMouseDown { evt ->
                        sheetStartY = sheetPointerY(evt.nativeEvent)
                        sheetDy = 0.0
                        sheetDragging = true
                        evt.preventDefault()
                    }
                    onTouchStart { evt ->
                        sheetStartY = sheetPointerY(evt.nativeEvent)
                        sheetDy = 0.0
                        sheetDragging = true
                    }
                },
            contentAlignment = Alignment.Center,
        ) {
            Box(
                Modifier
                    .width(44.px)
                    .height(5.px)
                    .borderRadius(999.px)
                    .backgroundColor(Color.rgba(31, 36, 33, 0.16f)),
            )
        }

        // Header
        Row(
            Modifier
                .fillMaxWidth()
                .margin(bottom = 12.px)
                .cursor(Cursor.Grab)
                .styleModifier { property("touch-action", "none") }
                .attrsModifier {
                    onMouseDown { evt ->
                        sheetStartY = sheetPointerY(evt.nativeEvent)
                        sheetDy = 0.0
                        sheetDragging = true
                    }
                    onTouchStart { evt ->
                        sheetStartY = sheetPointerY(evt.nativeEvent)
                        sheetDy = 0.0
                        sheetDragging = true
                    }
                },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    AdminT.tableN(table.label),
                    Modifier.fontSize(21.px).fontWeight(FontWeight.Bold)
                        .color(Brand.Charcoal).margin(right = 10.px)
                        .attrsModifier { classes("qk-display") },
                )
                if (orders.isEmpty()) {
                    AdminBadge(AdminT.tableFree, "success")
                    // A table can only be removed while it is free, and only by a manager.
                    onDelete?.let { delete ->
                        IconAction(AdminIcons.Trash, AdminT.deleteTable(table.label), danger = true, onClick = delete)
                    }
                } else {
                    AdminBadge(formatMinor(orders.sumOf { it.totalMinor }, currency), "warning")
                }
            }
            Box(
                Modifier
                    .size(32.px)
                    .minWidth(32.px)
                    .borderRadius(999.px)
                    .backgroundColor(Brand.Surface)
                    .cursor(Cursor.Pointer)
                    .attrsModifier { classes("qk-press"); attr("role", "button"); attr("aria-label", AdminT.closeLabel) }
                    .onClick { onClose() },
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(MenuIcons.Close, 14, "#6B7280")
            }
        }

        if (pickerOpen) {
            // ---- Dish picker ----
            AdminTitle(AdminT.newOrderTitle)
            TextInput(value = search, onValueChange = { search = it }, placeholder = AdminT.searchDishPh)
            val items = menuItems.orEmpty().filter {
                search.isBlank() || it.name.localized("uk").contains(search, true)
            }
            Column(Modifier.fillMaxWidth().margin(top = 8.px)) {
                items.forEach { item ->
                    val qty = draft[item.id] ?: 0
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .backgroundColor(Brand.Surface)
                            .borderRadius(11.px)
                            .padding(topBottom = 8.px, leftRight = 10.px)
                            .margin(bottom = 6.px),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(Modifier.margin(right = 8.px)) {
                            SpanText(
                                item.name.localized("uk"),
                                Modifier.fontSize(13.px).fontWeight(FontWeight.Medium).color(Brand.Charcoal),
                            )
                            SpanText(
                                formatMinor(item.priceMinor, currency),
                                Modifier.fontSize(11.px).fontWeight(FontWeight.SemiBold).color(Brand.Emerald),
                            )
                        }
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            if (qty > 0) {
                                QtyButton("−") { draft = draft + (item.id to qty - 1) }
                                SpanText(
                                    "$qty",
                                    Modifier.fontSize(14.px).fontWeight(FontWeight.Bold)
                                        .color(Brand.Charcoal).margin(leftRight = 9.px),
                                )
                            }
                            QtyButton("+") { draft = draft + (item.id to qty + 1) }
                        }
                    }
                }
            }
            val draftItems = draft.filterValues { it > 0 }
            val draftTotal = draftItems.entries.sumOf { (id, qty) ->
                (menuItems.orEmpty().firstOrNull { it.id == id }?.priceMinor ?: 0) * qty
            }
            Row(
                Modifier.fillMaxWidth().margin(topBottom = 10.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    "${AdminT.totalLabel}: ${formatMinor(draftTotal, currency)}",
                    Modifier.fontSize(15.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal),
                )
            }
            AdminButton(
                text = AdminT.addOrderBtn,
                enabled = draftItems.isNotEmpty() && !busy,
                modifier = Modifier.fillMaxWidth(),
                onClick = {
                    busy = true
                    scope.launch {
                        val request = CreateOrderRequestDto(
                            tableId = table.id,
                            items = draftItems.mapNotNull { (id, qty) ->
                                menuItems.orEmpty().firstOrNull { it.id == id }?.let { item ->
                                    OrderItemRequestDto(
                                        itemId = item.id,
                                        name = item.name.localized("uk"),
                                        priceMinor = item.priceMinor,
                                        qty = qty,
                                    )
                                }
                            },
                        )
                        val created = runCatching { repository.createOrder(rid, request) }.getOrNull()
                        OrdersStore.refresh(repository, rid)
                        busy = false
                        if (created != null) {
                            draft = emptyMap()
                            search = ""
                            pickerOpen = false
                            toast.success(AdminT.addOrderBtn)
                        } else {
                            toast.error(AdminT.saveFailed)
                        }
                    }
                },
            )
            AdminButton(
                text = AdminT.cancel,
                secondary = true,
                modifier = Modifier.fillMaxWidth().margin(top = 6.px),
                onClick = { pickerOpen = false },
            )
        } else if (closeResult != null) {
            // ---- Loyalty summary after the bill was closed ----
            val result = closeResult!!
            Column(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(Brand.Surface)
                    .borderRadius(14.px)
                    .padding(16.px)
                    .margin(bottom = 10.px)
                    .attrsModifier { classes("qk-rise") },
            ) {
                Row(Modifier.fillMaxWidth().margin(bottom = 10.px), verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        Modifier.size(38.px).minWidth(38.px).borderRadius(999.px).backgroundColor(Brand.GreenSoft),
                        contentAlignment = Alignment.Center,
                    ) {
                        InlineIcon(AdminIcons.Check, 18, "#2E9E5B")
                    }
                    Column(Modifier.margin(left = 10.px)) {
                        SpanText(
                            AdminT.markPaid,
                            Modifier.fontSize(15.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal),
                        )
                        if (result.displayName.isNotBlank()) {
                            SpanText(
                                result.displayName,
                                Modifier.fontSize(12.px).color(Brand.TextSecondary),
                            )
                        }
                    }
                }
                listOf(
                    AdminT.loyaltyEarnedLabel to "+${formatMoneyMinor(result.earnedMinor, currency)}",
                    AdminT.loyaltyRedeemedLabel to "−${formatMoneyMinor(result.redeemedMinor, currency)}",
                    AdminT.loyaltyNewBalanceLabel to formatMoneyMinor(result.balanceMinor, currency),
                ).forEach { (label, value) ->
                    Row(
                        Modifier.fillMaxWidth().margin(bottom = 4.px),
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        SpanText(label, Modifier.fontSize(13.px).color(Brand.TextSecondary))
                        SpanText(
                            value,
                            Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(Brand.Charcoal),
                        )
                    }
                }
            }
            AdminButton(
                text = AdminT.done,
                modifier = Modifier.fillMaxWidth(),
                onClick = { onClose() },
            )
        } else {
            // ---- Orders of the table ----
            // Proactive guest card: who's here + their loyalty, updated on scan/call.
            if (restaurant.loyaltyEnabled) {
                GuestPresenceCard(candidates, currency)
            }
            if (orders.isEmpty()) {
                AdminEmpty(MenuIcons.Receipt, AdminT.tableFree, AdminT.addDishesHint)
            }
            orders.sortedBy { it.createdMs() }.forEach { order ->
                WaiterOrderCard(
                    order = order,
                    currency = currency,
                    canAct = ownsOrder(order),
                    onServe = {
                        scope.launch {
                            runCatching { repository.setOrderStatus(rid, order.id, "SERVED") }
                            OrdersStore.refresh(repository, rid)
                        }
                    },
                    onDelete = {
                        scope.launch {
                            runCatching { repository.deleteOrder(rid, order.id) }
                            OrdersStore.refresh(repository, rid)
                        }
                    },
                )
            }
            AdminButton(
                text = "＋ ${AdminT.newOrderTitle}",
                dark = true,
                modifier = Modifier.fillMaxWidth().margin(top = 4.px),
                onClick = { pickerOpen = true },
            )
            if (orders.isNotEmpty()) {
                Row(
                    Modifier.fillMaxWidth().margin(top = 14.px, bottom = 8.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        "${AdminT.legendBill} · ${AdminT.totalLabel}",
                        Modifier.fontSize(13.px).color(Brand.TextSecondary),
                    )
                    SpanText(
                        formatMinor(orders.sumOf { it.totalMinor }, currency),
                        Modifier.fontSize(19.px).fontWeight(FontWeight.Bold)
                            .color(Brand.Charcoal).attrsModifier { classes("qk-display") },
                    )
                }

                // Optional loyalty step; skipping it keeps closing the bill one click.
                if (restaurant.loyaltyEnabled) {
                    LoyaltyCloseBlock(
                        currency = currency,
                        expanded = loyaltyExpanded,
                        candidates = candidates,
                        codeInput = codeInput,
                        identified = identified,
                        redeemInput = redeemInput,
                        error = loyaltyError,
                        identifyBusy = identifyBusy,
                        onExpand = { loyaltyExpanded = true },
                        onCode = { input ->
                            codeInput = input
                            identified = null
                            loyaltyError = null
                        },
                        onScan = { scanOpen = true },
                        onIdentify = { identify() },
                        onRedeem = { redeemInput = it },
                        onClear = {
                            codeInput = ""
                            identified = null
                            redeemInput = ""
                            loyaltyError = null
                        },
                    )
                }

                if (!canCloseTable) {
                    SpanText(
                        AdminT.closeMixedTableHint,
                        Modifier.fillMaxWidth().margin(bottom = 8.px).fontSize(12.px)
                            .color(Brand.TextSecondary)
                            .styleModifier { property("text-align", "center") },
                    )
                }
                AdminButton(
                    text = AdminT.markPaid,
                    enabled = !busy && canCloseTable,
                    modifier = Modifier.fillMaxWidth(),
                    iconSvg = AdminIcons.Check,
                    onClick = {
                        if (window.confirm(AdminT.confirmPaid)) {
                            busy = true
                            val loyaltyCode =
                                if (restaurant.loyaltyEnabled) normalizeLoyaltyCode(codeInput) else null
                            // Redeem amount is entered in major units; clamp to the guest's balance.
                            val redeemMinor =
                                if (loyaltyCode == null) {
                                    null
                                } else {
                                    identified?.let { guest ->
                                        redeemInput.replace(',', '.').toDoubleOrNull()
                                            ?.let { (it * 100).roundToInt() }
                                            ?.coerceIn(0, guest.balanceMinor)
                                            ?.takeIf { it > 0 }
                                    }
                                }
                            scope.launch {
                                val result =
                                    runCatching {
                                        repository.closeTableOrders(rid, table.id, loyaltyCode, redeemMinor)
                                    }.getOrElse { CloseTableResult.Failed }
                                busy = false
                                when (result) {
                                    is CloseTableResult.Closed -> {
                                        OrdersStore.refresh(repository, rid)
                                        val loyalty = result.loyalty
                                        if (loyalty != null) {
                                            closeResult = loyalty
                                        } else {
                                            toast.success(AdminT.markPaid)
                                            onClose()
                                        }
                                    }
                                    CloseTableResult.LoyaltyCodeExpired -> {
                                        // Orders stay open on the backend; ask for a fresh code.
                                        loyaltyExpanded = true
                                        identified = null
                                        loyaltyError = AdminT.loyaltyCodeExpired
                                    }
                                    CloseTableResult.Failed -> toast.error(AdminT.updateFailed)
                                }
                            }
                        }
                    },
                )
            }
        }
    }

    if (scanOpen) {
        QrScanOverlay(
            onCode = { code ->
                codeInput = code
                identified = null
                loyaltyError = null
                scanOpen = false
                identify()
            },
            onClose = { scanOpen = false },
        )
    }
}

/** Two-letter initials for the avatar fallback when a guest has no photo. */
private fun guestInitials(name: String): String {
    val parts = name.trim().split(Regex("\\s+")).filter { it.isNotEmpty() }
    return when {
        parts.isEmpty() -> "?"
        parts.size == 1 -> parts[0].take(2).uppercase()
        else -> (parts[0].take(1) + parts[1].take(1)).uppercase()
    }
}

/**
 * "Guest at the table": the loyalty guests seen at this table, freshest scan first —
 * whoever just rang the bell floats to the top with a badge. Each shows avatar, how
 * many times they've visited and their cashback, so the waiter knows them up front.
 */
@Composable
private fun GuestPresenceCard(
    candidates: List<LoyaltyCandidateDto>?,
    currency: String,
) {
    if (candidates.isNullOrEmpty()) return
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(14.px)
            .padding(12.px)
            .margin(bottom = 10.px)
            .attrsModifier { classes("qk-rise") },
    ) {
        Row(
            Modifier.fillMaxWidth().margin(bottom = 8.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(MenuIcons.Sparkles, 15, "#E8A33D")
            SpanText(
                AdminT.loyaltyGuestAtTable,
                Modifier.fontSize(11.px).fontWeight(FontWeight.SemiBold).color(Brand.TextSecondary)
                    .margin(left = 8.px)
                    .styleModifier {
                        property("text-transform", "uppercase")
                        property("letter-spacing", "0.05em")
                    },
            )
        }
        candidates.forEachIndexed { index, candidate ->
            if (index > 0) {
                Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(Brand.Divider).margin(topBottom = 8.px))
            }
            GuestPresenceRow(candidate, currency)
        }
    }
}

@Composable
private fun GuestPresenceRow(
    candidate: LoyaltyCandidateDto,
    currency: String,
) {
    val accent = Color.rgb(255, 122, 89)
    val name = candidate.displayName.ifBlank { candidate.userId.take(8) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(Modifier.gap(10.px), verticalAlignment = Alignment.CenterVertically) {
            // Avatar: the OAuth photo when present, else initials on a soft disc.
            Box(
                Modifier.size(38.px).minWidth(38.px).borderRadius(999.px)
                    .backgroundColor(Brand.GreenSoft)
                    .styleModifier {
                        property("background-size", "cover")
                        property("background-position", "center")
                        if (!candidate.avatarUrl.isNullOrBlank()) {
                            property("background-image", "url('${candidate.avatarUrl}')")
                        }
                    },
                contentAlignment = Alignment.Center,
            ) {
                if (candidate.avatarUrl.isNullOrBlank()) {
                    SpanText(
                        guestInitials(name),
                        Modifier.fontSize(13.px).fontWeight(FontWeight.Bold).color(Brand.Emerald),
                    )
                }
            }
            Column {
                SpanText(
                    name,
                    Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(Brand.Charcoal),
                )
                if (candidate.calling) {
                    Row(Modifier.gap(4.px), verticalAlignment = Alignment.CenterVertically) {
                        InlineIcon(
                            if (candidate.callType == "BILL") MenuIcons.Receipt else MenuIcons.Bell,
                            12,
                            "#FF7A59",
                        )
                        SpanText(
                            if (candidate.callType == "BILL") AdminT.loyaltyCallingBill else AdminT.loyaltyCallingWaiter,
                            Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(accent),
                        )
                    }
                } else {
                    SpanText(
                        if (candidate.visitCount <= 0) {
                            AdminT.loyaltyNewGuest
                        } else {
                            "${candidate.visitCount} ${AdminT.loyaltyVisitsSuffix}"
                        },
                        Modifier.fontSize(12.px).color(Brand.TextSecondary),
                    )
                }
            }
        }
        SpanText(
            formatMoneyMinor(candidate.balanceMinor, currency),
            Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(Brand.Emerald),
        )
    }
}

/**
 * Loyalty step inside the bill: a "recently scanned here" hint, the guest's
 * 6-digit code (typed or scanned), and an optional redeem amount after identify.
 */
@Composable
private fun LoyaltyCloseBlock(
    currency: String,
    expanded: Boolean,
    candidates: List<LoyaltyCandidateDto>?,
    codeInput: String,
    identified: LoyaltyGuestDto?,
    redeemInput: String,
    error: String?,
    identifyBusy: Boolean,
    onExpand: () -> Unit,
    onCode: (String) -> Unit,
    onScan: () -> Unit,
    onIdentify: () -> Unit,
    onRedeem: (String) -> Unit,
    onClear: () -> Unit,
) {
    if (!expanded) {
        // Collapsed: closing without loyalty stays exactly one click below.
        Row(
            Modifier
                .fillMaxWidth()
                .backgroundColor(Brand.Surface)
                .borderRadius(11.px)
                .padding(topBottom = 10.px, leftRight = 12.px)
                .margin(bottom = 8.px)
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qk-press"); attr("role", "button") }
                .onClick { onExpand() },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(MenuIcons.Sparkles, 15, "#E8A33D")
            SpanText(
                AdminT.loyaltyGuestTitle,
                Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold)
                    .color(Brand.Charcoal).margin(left = 8.px),
            )
        }
        return
    }

    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(11.px)
            .padding(12.px)
            .margin(bottom = 8.px)
            .attrsModifier { classes("qk-rise") },
    ) {
        Row(
            Modifier.fillMaxWidth().margin(bottom = 8.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                InlineIcon(MenuIcons.Sparkles, 15, "#E8A33D")
                SpanText(
                    AdminT.loyaltyGuestTitle,
                    Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold)
                        .color(Brand.Charcoal).margin(left = 8.px),
                )
            }
            if (codeInput.isNotBlank() || identified != null) {
                SpanText(
                    AdminT.loyaltyClear,
                    Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(Brand.TextSecondary)
                        .cursor(Cursor.Pointer)
                        .attrsModifier { classes("qk-press"); attr("role", "button") }
                        .onClick { onClear() },
                )
            }
        }

        // Display-only hint: loyalty guests who scanned this table's QR recently.
        if (!candidates.isNullOrEmpty()) {
            SpanText(
                AdminT.loyaltyCandidatesTitle,
                Modifier.fontSize(11.px).fontWeight(FontWeight.SemiBold).color(Brand.TextSecondary)
                    .margin(bottom = 4.px)
                    .styleModifier {
                        property("text-transform", "uppercase")
                        property("letter-spacing", "0.05em")
                    },
            )
            candidates.forEach { candidate ->
                Row(
                    Modifier.fillMaxWidth().margin(bottom = 3.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        candidate.displayName.ifBlank { candidate.userId.take(8) },
                        Modifier.fontSize(13.px).color(Brand.Charcoal),
                    )
                    SpanText(
                        formatMoneyMinor(candidate.balanceMinor, currency),
                        Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(Brand.Emerald),
                    )
                }
            }
            Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(Brand.Divider).margin(topBottom = 8.px))
        }

        if (identified == null) {
            // Scan-only: one full-width button, no manual code entry.
            AdminButton(
                text = AdminT.loyaltyScanQr,
                secondary = true,
                iconSvg = AdminIcons.QrCode,
                modifier = Modifier.fillMaxWidth(),
                onClick = onScan,
            )
        } else {
            Row(
                Modifier.fillMaxWidth().margin(bottom = 6.px),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    identified.displayName.ifBlank { identified.userId.take(8) },
                    Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(Brand.Charcoal),
                )
                AdminBadge(
                    "${AdminT.loyaltyBalanceLabel}: ${formatMoneyMinor(identified.balanceMinor, currency)}",
                    "success",
                )
            }
            Box(Modifier.width(200.px)) {
                TextInput(
                    value = redeemInput,
                    onValueChange = { input ->
                        onRedeem(input.filter { it.isDigit() || it == '.' || it == ',' }.take(9))
                    },
                    label = AdminT.loyaltyRedeemLabel,
                    placeholder = "0.00",
                )
            }
        }

        AdminError(error)
    }
}

/**
 * Fullscreen camera overlay scanning for a `QRKL:{code}` (or bare 6-digit) QR
 * with the browser BarcodeDetector API; closes itself on a match.
 */
@Composable
private fun QrScanOverlay(
    onCode: (String) -> Unit,
    onClose: () -> Unit,
) {
    var failed by remember { mutableStateOf(false) }

    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .backgroundColor(Color.rgba(15, 20, 17, 0.82f))
            .zIndex(70)
            .attrsModifier { classes("qk-fade-in") }
            .onClick { onClose() },
        contentAlignment = Alignment.Center,
    ) {
        Column(
            Modifier
                .onClick { it.stopPropagation() },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Div(
                attrs = {
                    style {
                        property("width", "min(320px, 84vw)")
                        property("height", "min(320px, 84vw)")
                        property("border-radius", "18px")
                        property("overflow", "hidden")
                        property("background", "#000")
                    }
                    ref { el ->
                        var stopped = false
                        var stream: dynamic = null
                        val video = document.createElement("video") as HTMLVideoElement
                        video.setAttribute("playsinline", "")
                        video.setAttribute("muted", "")
                        video.style.width = "100%"
                        video.style.height = "100%"
                        video.style.setProperty("object-fit", "cover")
                        el.appendChild(video)

                        fun stopStream() {
                            stopped = true
                            val s = stream
                            if (s != null) {
                                (s.getTracks() as Array<dynamic>).forEach { it.stop() }
                            }
                        }

                        val detector: dynamic =
                            try {
                                js("new BarcodeDetector({ formats: ['qr_code'] })")
                            } catch (t: Throwable) {
                                null
                            }
                        val mediaDevices = window.navigator.asDynamic().mediaDevices
                        if (detector == null || mediaDevices == null || mediaDevices == undefined) {
                            failed = true
                        } else {
                            fun tick() {
                                if (stopped) return
                                detector.detect(video).then({ found: dynamic ->
                                    if (!stopped && found != null && (found.length as Int) > 0) {
                                        val raw = found[0].rawValue as? String
                                        val code = raw?.let { normalizeLoyaltyCode(it) }
                                        if (code != null) {
                                            stopStream()
                                            onCode(code)
                                        }
                                    }
                                }, { })
                                window.setTimeout({ tick() }, 350)
                            }
                            mediaDevices.getUserMedia(js("({ video: { facingMode: 'environment' } })"))
                                .then({ s: dynamic ->
                                    if (stopped) {
                                        (s.getTracks() as Array<dynamic>).forEach { it.stop() }
                                    } else {
                                        stream = s
                                        video.asDynamic().srcObject = s
                                        video.play()
                                        tick()
                                    }
                                }, { failed = true })
                        }
                        onDispose { stopStream() }
                    }
                },
            )
            if (failed) {
                SpanText(
                    AdminT.loyaltyCameraFailed,
                    Modifier.fontSize(13.px).color(Brand.Surface).margin(top = 12.px)
                        .styleModifier { property("text-align", "center") },
                )
            }
            AdminButton(
                text = AdminT.cancel,
                secondary = true,
                modifier = Modifier.margin(top = 14.px),
                onClick = onClose,
            )
        }
    }
}

@Composable
private fun WaiterOrderCard(
    order: OrderDto,
    currency: String,
    canAct: Boolean,
    onServe: () -> Unit,
    onDelete: () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(12.px)
            .padding(12.px)
            .margin(bottom = 8.px),
    ) {
        Row(
            Modifier.fillMaxWidth().margin(bottom = 6.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminBadge(statusLabel(order.status), statusTone(order.status))
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    formatMinor(order.totalMinor, currency),
                    Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(Brand.Charcoal),
                )
                if (order.status == "NEW" && canAct) {
                    IconAction(AdminIcons.Trash, AdminT.delete, danger = true, onClick = onDelete)
                }
            }
        }
        order.items.forEach { item ->
            Row(
                Modifier.fillMaxWidth().margin(bottom = 2.px),
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                SpanText("${item.qty} × ${item.name}", Modifier.fontSize(13.px).color(Brand.Charcoal))
                SpanText(
                    formatMinor(item.priceMinor * item.qty, currency),
                    Modifier.fontSize(12.px).color(Brand.TextSecondary),
                )
            }
        }
        when {
            !canAct -> SpanText(
                AdminT.orderOtherWaiter,
                Modifier.fillMaxWidth().margin(top = 8.px).fontSize(12.px)
                    .color(Brand.TextSecondary)
                    .styleModifier { property("text-align", "center") },
            )
            order.status == "READY" -> AdminButton(
                text = AdminT.markServed,
                modifier = Modifier.fillMaxWidth().margin(top = 8.px),
                onClick = onServe,
            )
        }
    }
}

@Composable
private fun QtyButton(
    symbol: String,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .size(28.px)
            .minWidth(28.px)
            .borderRadius(999.px)
            .backgroundColor(Brand.GreenSoft)
            .border(1.px, LineStyle.Solid, Brand.Divider)
            .cursor(Cursor.Pointer)
            .attrsModifier { classes("qk-press"); attr("role", "button") }
            .onClick { onClick() },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(symbol, Modifier.fontSize(15.px).fontWeight(FontWeight.Bold).color(Brand.Green))
    }
}

/** Dragging the sheet header down past this many pixels dismisses it. */
private const val SHEET_CLOSE_DRAG_PX = 110.0

private fun sheetPointerY(e: org.w3c.dom.events.Event): Double {
    val d = e.asDynamic()
    return if (d.touches != null && (d.touches.length as Int) > 0) {
        d.touches[0].clientY.unsafeCast<Double>()
    } else {
        d.clientY.unsafeCast<Double>()
    }
}
